import React from 'react'
import { FileText, Image, File, ExternalLink } from 'lucide-react'
import { DocumentModel } from '@/models/templates/document.model'

interface Props {
  documents: DocumentModel[] | null
}

const primary = '#7b2a40'

export const DocumentViewer: React.FC<Props> = ({ documents }) => {
  if (!documents || documents.length === 0)
    return <p className="text-gray-500 text-sm">No documents submitted.</p>

  const getFileIcon = (contentType?: string | null) => {
    if (!contentType) return <FileText size={18} className="text-gray-500" />
    if (contentType.includes('image')) return <Image size={18} className="text-[#7b2a40]" />
    if (contentType.includes('pdf')) return <FileText size={18} className="text-[#7b2a40]" />
    return <File size={18} className="text-[#7b2a40]" />
  }

  const formatFileSize = (size?: string | null) => {
    if (!size) return '—'
    const num = parseInt(size, 10)
    if (isNaN(num)) return size
    if (num < 1024) return `${num} B`
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`
    return `${(num / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {documents.map((doc) => (
        <div
          key={doc.id ?? doc.storage_key ?? doc.url ?? Math.random()}
          className="border rounded-lg p-4 bg-white shadow-sm hover:shadow transition flex flex-col gap-3"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-gray-100">
              {getFileIcon(doc.content_type)}
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900 text-sm capitalize">
                {doc.type?.replace(/([A-Z])/g, ' $1') ?? 'Unknown'}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {doc.uploaded_at
                  ? new Date(doc.uploaded_at).toLocaleDateString()
                  : '—'}
              </p>
            </div>
          </div>

          <div className="text-xs text-gray-600 flex justify-between">
            <span>{formatFileSize(doc.size)}</span>
            <span className="truncate max-w-[60%] text-gray-400">
              {doc.content_type ?? '—'}
            </span>
          </div>

          {doc.url ? (
            <a
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1 text-sm font-medium mt-1 text-white rounded-md py-2 hover:opacity-90"
              style={{ backgroundColor: primary }}
            >
              <ExternalLink size={14} />
              View Document
            </a>
          ) : (
            <div className="text-center py-2 text-gray-400 text-sm border border-dashed rounded-md">
              Not Uploaded
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
